import { SyntheticEvent, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { DataProp } from 'editorjs-blocks-react-renderer';
import { CollectionItemProps } from '@/components/CollectionsList.tsx';
import { HistoryLoader } from '@/components/Loader.tsx';
import { TypoHeading, TypoHeading2 } from '@/components/Typography.tsx';
import { ArrowLeft2, PdfFile } from '@/components/Icons.tsx';
import VIDEO_QUERY from '@/graphql/queries/video.graphql';
import UPDATE_VIDEO_MUTATION from '@/graphql/mutations/update-video.graphql';
import VideoPlayer from '@/components/VideoPlayer';
import { toJSON } from '@/utils/helpers.tsx';
import BlocksRenderer from '@/components/BlocksRenderer.tsx';

const Video = () => {
  const { videoId = '' } = useParams();
  const [updateVideo] = useMutation(UPDATE_VIDEO_MUTATION);

  const onVideoAvailable = useCallback(({ video }: QueryResult) => {
    const clientVideo = {
      videoId: video.videoId,
      isCompleted: Boolean(video.clientVideo?.isCompleted),
      timesOpened: (video.clientVideo?.timesOpened ?? 0) + 1,
      durationElapsed: video.clientVideo?.durationElapsed ?? 0,
    };

    return updateVideo({ variables: { clientVideo } });
  }, [updateVideo]);

  const { video } = useVideo(Number(videoId), onVideoAvailable);

  if (!videoId) return <Navigate to="/video-tutorials" replace />;

  return (
    <>
      <TypoHeading2 className="text-neutral-text-strong font-medium px-10 pt-6 pb-4">
        Video Tutorials
      </TypoHeading2>
      <div className="w-full bg-neutral2 text-neutral2-text-soft rounded px-10 pt-4 pb-8">
        {video == null ? <HistoryLoader /> : <Content {...video} />}
      </div>
    </>
  );
};

const Content = ({
  id, description, image, name, stream, subtitles, attachments, clientVideo
}: VideoItemWithSource) => {
  const navigate = useNavigate();
  const subtitleData = { src: subtitles, label: 'English (US)', srclang: 'en-US' };
  const hasDescription = description != null && description.blocks.length > 0;

  const [updateVideo] = useMutation(UPDATE_VIDEO_MUTATION);

  const onTimeUpdate = useCallback(
    async (event: SyntheticEvent<HTMLVideoElement, Event>) => {
      const video = event.target as HTMLVideoElement;
      const exactTime = video.currentTime;
      const currentTime = Math.floor(exactTime);
      const duration = Math.floor(video.duration);
      const isCompleted = currentTime === duration;

      const input = {
        videoId: id,
        isCompleted: Boolean(clientVideo?.isCompleted ?? isCompleted),
        timesOpened: clientVideo?.timesOpened ?? 1,
        durationElapsed: (clientVideo?.durationElapsed ?? 0) + currentTime,
      };

      if (currentTime < 1) return;

      if (isCompleted || currentTime % 10 === 0) {
        const updated: any = await updateVideo({ variables: { clientVideo: input } });
        return updated;
      }
    }, [id, clientVideo, updateVideo]);

  return (
    <>
      <button
        onClick={() => navigate(-1)}
        className="flex flex-col md:flex-row items-center rounded px-4 py-2 gap-x-2 bg-shade-5 mt-2 mb-10"
      >
        <ArrowLeft2 width={16} height={16} /><span className="text-common-white text-callout">Back</span>
      </button>
      <div className="flex flex-col gap-y-3 mb-6">
        <VideoPlayer
          className="w-full"
          src={stream}
          poster={image}
          subtitles={subtitleData}
          onTimeUpdate={onTimeUpdate}
        />
        <span className="px-2 text-neutral-text-soft font-medium text-body">{name}</span>
      </div>
      {Boolean(attachments.length) && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          {attachments.map(file => (
            <DownloadPdfButton key={file.fileId} file={file} />
          ))}
        </div>
      )}
      {hasDescription && (
        <article className="flex flex-col gap-2">
          <TypoHeading className="font-medium">Description</TypoHeading>
          <BlocksRenderer
            data={description}
            config={{
              paragraph: {
                className: 'text-body font-primary',
                actionsClassNames: {
                  alignment: 'text-{alignment}',
                }
              },
            }}
          />
        </article>
      )}
    </>
  );
};

const DownloadPdfButton = ({ file }: { file: Attachment }) => {
  const downloadFile = useCallback(async () => {
    const source = await fetch(file.resource.resourceUrl);
    const blob = await source.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${file.name}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [file]);

  return (
    <button
      className="flex items-center justify-start gap-4 rounded border border-shade-10 px-3 py-1.5 hover:bg-shade-5"
      onClick={downloadFile}
    >
      <PdfFile className="w-8 h-8 text-neutral2-icon" />
      <span className="flex flex-col items-start text-left">
        <span className="text-neutral2-text-strong text-body font-normal line-clamp-1">{file.name}</span>
        <span className="text-caption text-primary">Download</span>
      </span>
    </button>
  );
};

const useVideo =
  (videoId: number, onCompleted: (data: QueryResult) => void): { loading: boolean, video?: VideoItemWithSource } => {
    const { data, loading } = useQuery<QueryResult>(VIDEO_QUERY, {
      variables: { videoId },
      onCompleted
    });

    return useMemo(() => ({
      loading,
      video: data?.video && {
        id: videoId,
        image: data.video.image,
        name: data.video.name,
        description: toJSON(data.video.description) || null,
        stream: data.video.stream,
        subtitles: data.video.subtitles,
        attachments: data.video.attachments,
        clientVideo: data.video.clientVideo,
        type: 'video',
      }
    }), [data?.video, loading, videoId]);
  };

type Attachment = {
  fileId: number;
  name: string;
  resource: {
    resourceUrl: string;
  }
};

type ClientVideo = {
  timesOpened: number;
  durationElapsed: number;
  isCompleted: boolean;
} | null;

interface VideoItemWithSource extends CollectionItemProps {
  stream: string;
  subtitles: string;
  description: DataProp | null;
  attachments: Attachment[],
  clientVideo: ClientVideo,
}

interface QueryResult {
  video: {
    videoId: number;
    image: string;
    name: string;
    description: string;
    stream: string;
    subtitles: string;
    attachments: Attachment[];
    clientVideo: ClientVideo;
  };
}

export default Video;
